const KLAVIYO_PROXY_BASE = '/apps/klaviyo-proxy';
const KLAVIYO_CS_EVENT_NAME = 'Coming Soon Signup';
const KLAVIYO_PROXY_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

class PdpNotify extends HTMLElement {
  connectedCallback() {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.overlay = this.querySelector('[data-pdp-notify-overlay]');
    this.dialog = this.overlay?.querySelector('.pdp-notify__dialog');
    this.form = this.overlay?.querySelector('[data-pdp-notify-form]');
    this.emailInput = this.overlay?.querySelector('input[name="email"]');
    this.errorEl = this.overlay?.querySelector('[data-pdp-notify-error]');
    this.successEl = this.overlay?.querySelector('[data-pdp-notify-success]');
    this.formState = this.overlay?.querySelector('[data-pdp-notify-form-state]');
    this.submitBtn = this.overlay?.querySelector('[data-pdp-notify-submit]');
    this.imageEl = this.overlay?.querySelector('.pdp-notify__thumb-img');
    this.variants = this.readVariants();
    this.lastFocus = null;

    this.querySelector('[data-pdp-notify-open]')?.addEventListener('click', () => this.open(), { signal });
    this.overlay?.querySelector('[data-pdp-notify-close]')?.addEventListener('click', () => this.close(), { signal });
    this.overlay?.addEventListener('click', (event) => {
      if (event.target === this.overlay) this.close();
    }, { signal });
    this.form?.addEventListener('submit', (event) => this.onSubmit(event), { signal, capture: true });
    this.emailInput?.addEventListener('input', () => {
      if (this.emailInput.classList.contains('is-invalid')) this.validateEmail(false);
    }, { signal });
    this.emailInput?.addEventListener('blur', () => this.validateEmail(false), { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    }, { signal });

    const productInfo = this.closest('product-info');
    const productForm = productInfo?.querySelector('form[is="product-form"]');
    productForm?.addEventListener('variant:change', (event) => {
      this.syncFromVariant(event.detail?.variant);
    }, { signal });

    if (window.theme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
      this.unsubscribeVariant = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.productId) !== this.getAttribute('data-product-id')) return;
        this.syncFromVariant(data.variant);
      });
    }

    this.overlay?.querySelectorAll('.pdp-notify__privacy a').forEach((link) => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener');
    });

    this.syncVisibility(this.getAttribute('data-mode'));
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.unsubscribeVariant?.();
    if (this.isOpen) this.close();
  }

  get isOpen() {
    return this.overlay && !this.overlay.hasAttribute('hidden');
  }

  readVariants() {
    const node = this.querySelector('[data-pdp-notify-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return [];
    }
  }

  message(type) {
    const prefix = this.getAttribute('data-mode') === 'coming-soon' ? 'cs' : 'bis';
    return this.getAttribute(`data-${prefix}-${type}-message`) || '';
  }

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  validateEmail(showEmptyError) {
    const email = (this.emailInput?.value || '').trim();
    if (!email) {
      if (showEmptyError) {
        this.setEmailInvalid(true);
        this.showError(this.message('invalid'));
      } else {
        this.setEmailInvalid(false);
        this.hideError();
      }
      return false;
    }

    if (!this.isValidEmail(email)) {
      this.setEmailInvalid(true);
      this.showError(this.message('invalid'));
      return false;
    }

    this.setEmailInvalid(false);
    this.hideError();
    return true;
  }

  setEmailInvalid(invalid) {
    if (!this.emailInput) return;
    this.emailInput.classList.toggle('is-invalid', invalid);
    this.emailInput.setAttribute('aria-invalid', invalid ? 'true' : 'false');
  }

  syncFromVariant(variant) {
    if (!variant) return;

    const comingSoon = this.getAttribute('data-coming-soon') === 'true';
    const matched = this.variants.find((item) => String(item.id) === String(variant.id)) || variant;
    const available = matched.available !== false && variant.available !== false;

    this.setAttribute('data-variant-id', String(variant.id));
    if (matched.sku != null) this.setAttribute('data-sku', matched.sku);

    const imageUrl = matched.featured_image || this.getAttribute('data-fallback-image');
    if (this.imageEl && imageUrl) this.imageEl.src = imageUrl;

    let mode = '';
    if (comingSoon) mode = 'coming-soon';
    else if (!available) mode = 'back-in-stock';

    this.setAttribute('data-mode', mode);
    this.syncVisibility(mode);
    if (!mode && this.isOpen) this.close();
  }

  syncVisibility(mode) {
    const visible = mode === 'coming-soon' || mode === 'back-in-stock';
    this.classList.toggle('is-visible', visible);

    const modeNodes = [
      ...this.querySelectorAll('[data-for-mode]'),
      ...(this.overlay?.querySelectorAll('[data-for-mode]') || []),
    ];
    modeNodes.forEach((node) => {
      node.hidden = node.getAttribute('data-for-mode') !== mode;
    });

    const buybox = this.querySelector('.pdp-notify__buybox');
    if (buybox) buybox.hidden = !visible;

    const placeholder = this.emailInput?.getAttribute(`data-placeholder-${mode}`);
    if (this.emailInput && placeholder != null) this.emailInput.placeholder = placeholder;

    const sticky = document.querySelector(`product-sticky-form#ProductStickyForm-${this.sectionId()}-${this.getAttribute('data-product-id')}`);
    if (sticky) sticky.hidden = visible;
  }

  sectionId() {
    return this.closest('product-info')?.getAttribute('data-section-id') || '';
  }

  open() {
    if (!this.overlay) return;
    this.lastFocus = document.activeElement;
    this.resetFormState();
    if (this.overlay.parentElement !== document.body) {
      this.overlayPlaceholder = this.overlay.parentElement;
      document.body.append(this.overlay);
    }
    this.overlay.hidden = false;
    document.body.classList.add('no-scroll');
    requestAnimationFrame(() => {
      this.overlay.classList.add('is-open');
      (this.emailInput || this.dialog)?.focus();
    });
  }

  close() {
    if (!this.overlay) return;
    this.overlay.classList.remove('is-open');
    this.unlock();
    this.overlay.hidden = true;
    if (this.overlayPlaceholder && this.overlay.parentElement === document.body) {
      this.overlayPlaceholder.append(this.overlay);
      this.overlayPlaceholder = null;
    }
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
  }

  unlock() {
    document.body.classList.remove('no-scroll');
  }

  resetFormState() {
    if (this.formState) this.formState.hidden = false;
    if (this.successEl) this.successEl.hidden = true;
    this.hideError();
    this.setEmailInvalid(false);
    this.setSubmitBusy(false);
  }

  showSuccess(email) {
    this.successEl?.querySelectorAll('[data-pdp-notify-success-body]').forEach((node) => {
      if (!node.dataset.template) node.dataset.template = node.textContent;
      node.textContent = node.dataset.template.replaceAll('[email]', email);
    });
    if (this.formState) this.formState.hidden = true;
    if (this.successEl) this.successEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  setSubmitBusy(busy) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = busy;
    if (busy) this.submitBtn.setAttribute('aria-busy', 'true');
    else this.submitBtn.removeAttribute('aria-busy');
  }

  profileAttributes(email) {
    const attributes = { email };
    const locale = this.getAttribute('data-locale');
    if (locale) attributes.locale = locale;
    return attributes;
  }

  async onSubmit(event) {
    event.preventDefault();
    this.hideError();

    const email = (this.emailInput?.value || '').trim();
    if (!this.validateEmail(true)) {
      event.stopImmediatePropagation();
      this.setSubmitBusy(false);
      this.emailInput?.focus();
      return;
    }

    const mode = this.getAttribute('data-mode');
    const newsletter = this.form?.querySelector(`[data-for-mode="${mode}"] input[name="newsletter"]`)?.checked;
    const listId = mode === 'coming-soon'
      ? this.getAttribute('data-cs-list-id')
      : this.getAttribute('data-bis-list-id');

    this.setSubmitBusy(true);

    try {
      if (mode === 'coming-soon') {
        await this.createClientEvent(email);
      } else {
        await this.createClientBis(email);
      }

      if (newsletter && listId) {
        await this.createClientSubscription(email, listId);
      }

      await this.createProfileImport(email);

      this.setSubmitBusy(false);
      this.showSuccess(email);
    } catch (error) {
      this.showError(this.message('error'));
      this.setSubmitBusy(false);
    }
  }

  async createClientEvent(email) {
    const response = await fetch(`${KLAVIYO_PROXY_BASE}/events`, {
      method: 'POST',
      headers: KLAVIYO_PROXY_HEADERS,
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            unique_id: crypto.randomUUID(),
            properties: {
              product_id: this.getAttribute('data-product-id'),
              product_title: this.getAttribute('data-product-title'),
              SKU: this.getAttribute('data-sku') || '',
            },
            metric: {
              data: {
                type: 'metric',
                attributes: { name: KLAVIYO_CS_EVENT_NAME },
              },
            },
            profile: {
              data: {
                type: 'profile',
                attributes: this.profileAttributes(email),
              },
            },
          },
        },
      }),
    });

    if (!response.ok) throw new Error('event');
  }

  async createClientBis(email) {
    const variantId = this.getAttribute('data-variant-id');
    const response = await fetch(`${KLAVIYO_PROXY_BASE}/back-in-stock-subscriptions`, {
      method: 'POST',
      headers: KLAVIYO_PROXY_HEADERS,
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      }),
    });

    if (!response.ok) throw new Error('bis');
  }

  async createProfileImport(email) {
    const locale = this.getAttribute('data-locale');
    if (!locale) return;

    const response = await fetch(`${KLAVIYO_PROXY_BASE}/profile-import`, {
      method: 'POST',
      headers: KLAVIYO_PROXY_HEADERS,
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: {
            email,
            locale,
          },
        },
      }),
    });

    if (!response.ok) throw new Error('profile-import');
  }

  async createClientSubscription(email, listId) {
    const response = await fetch(`${KLAVIYO_PROXY_BASE}/subscriptions`, {
      method: 'POST',
      headers: KLAVIYO_PROXY_HEADERS,
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'PDP Notify',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId,
              },
            },
          },
        },
      }),
    });

    if (!response.ok) throw new Error('subscription');
  }
}

if (!customElements.get('pdp-notify')) {
  customElements.define('pdp-notify', PdpNotify);
}
